import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";
import { UpgradeDialog } from "./UpgradeDialog";

const In = ({ label, testid, ...p }) => (
  <label className="block text-sm">{label}<input className="input w-full mt-1" data-testid={testid} {...p} /></label>
);
const copy = (v) => { navigator.clipboard?.writeText(v); toast.success(tr("crm.copied")); };

function useCfg(path) {
  const [d, setD] = useState(null);
  const load = () => api.get(path).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (body) => {
    try { const { data } = await api.put(path, body); setD(data); toast.success(tr("crm.saved")); return data; }
    catch (e) { toast.error(formatApiError(e)); return null; }
  };
  return [d, setD, save];
}

function Locked() {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-3 mb-4 border border-amber-300 bg-amber-50 text-sm flex items-center gap-2" data-testid="crm-locked">
      <Lock size={14} /> {tr("crm.locked")} <button className="underline" onClick={() => setOpen(true)} data-testid="crm-upgrade">{tr("crm.see_plans")}</button>
      {open && <UpgradeDialog flag="whatsapp_crm" onClose={() => setOpen(false)} />}
    </div>
  );
}

export function LeadsSettingsPanel() {
  const [d, setD, save] = useCfg("/crm/leads-config");
  if (!d) return null;
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div className="space-y-3 max-w-xl" data-testid="leads-settings">
      {!d.crm && <Locked />}
      <label className="block text-sm">{tr("crm.assign_mode")}
        <select className="input h-10 w-full mt-1" value={d.assign_mode} onChange={set("assign_mode")} data-testid="leads-assign-mode">
          <option value="round_robin">{tr("crm.rr")}</option><option value="manager">{tr("crm.manager")}</option>
        </select>
      </label>
      {d.branches.length > 1 && d.branches.map((b) => (
        <label key={b.id} className="block text-sm">{b.name}
          <select className="input h-10 w-full mt-1" value={b.assign_mode || d.assign_mode} data-testid={`branch-assign-${b.id}`}
            onChange={(e) => setD({ ...d, branches: d.branches.map((x) => (x.id === b.id ? { ...x, assign_mode: e.target.value } : x)) })}>
            <option value="round_robin">{tr("crm.rr")}</option><option value="manager">{tr("crm.manager")}</option>
          </select>
        </label>
      ))}
      <In label={tr("crm.response_minutes")} type="number" min={5} value={d.response_minutes} onChange={set("response_minutes")} testid="leads-response-minutes" />
      <div className="grid grid-cols-2 gap-2">
        <In label={tr("crm.work_start")} type="time" value={d.work_start} onChange={set("work_start")} testid="leads-work-start" />
        <In label={tr("crm.work_end")} type="time" value={d.work_end} onChange={set("work_end")} testid="leads-work-end" />
      </div>
      <button className="btn-primary" disabled={!d.crm} data-testid="leads-settings-save" onClick={() => save({
        assign_mode: d.assign_mode, response_minutes: Number(d.response_minutes), work_start: d.work_start, work_end: d.work_end,
        branch_modes: Object.fromEntries(d.branches.map((b) => [b.id, b.assign_mode || d.assign_mode])),
      })}>{tr("crm.save")}</button>
    </div>
  );
}

function SecretField({ label, saved, value, onChange, onRemove, testid }) {
  return saved && value === undefined ? (
    <div className="text-sm" data-testid={`${testid}-saved`}>{label}: <span className="font-mono">{tr("crm.saved_hint", { p0: saved })}</span>
      <button className="btn-ghost text-xs ms-2" onClick={() => onChange("")} data-testid={`${testid}-replace`}>{tr("crm.replace")}</button>
      <button className="btn-ghost text-xs" onClick={onRemove} data-testid={`${testid}-remove`}>{tr("crm.remove")}</button></div>
  ) : <In label={label} type="password" autoComplete="off" value={value || ""} onChange={(e) => onChange(e.target.value)} testid={testid} />;
}

export function WhatsAppSettingsPanel() {
  const [d, setD, save] = useCfg("/crm/whatsapp");
  const [tok, setTok] = useState(undefined);
  const [secret, setSecret] = useState(undefined);
  const [result, setResult] = useState(null);
  if (!d) return null;
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const ar = d.auto_reply || {};
  const setAr = (k, v) => setD({ ...d, auto_reply: { ...ar, [k]: v } });
  const run = async (path) => { try { const { data } = await api.post(path); setResult(data); if (data.templates) setD({ ...d, templates: data.templates }); } catch (e) { toast.error(formatApiError(e)); } };
  const map = d.event_map || {};
  const setMap = (ev, k, v) => setD({ ...d, event_map: { ...map, [ev]: { ...(map[ev] || {}), [k]: v } } });
  const names = [...new Set((d.templates || []).map((t) => t.name))];
  const doSave = async () => {
    const body = { waba_id: d.waba_id, phone_number_id: d.phone_number_id, display_phone: d.display_phone, auto_reply: ar, event_map: map };
    if (tok) body.token = tok;
    if (secret) body.app_secret = secret;
    if (await save(body)) { setTok(undefined); setSecret(undefined); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="whatsapp-settings">
      {!d.crm && <Locked />}
      <p className="text-sm opacity-80">{tr("crm.wa_intro")}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <In label={tr("crm.waba_id")} value={d.waba_id || ""} onChange={set("waba_id")} testid="wa-waba-id" />
        <In label={tr("crm.phone_id")} value={d.phone_number_id || ""} onChange={set("phone_number_id")} testid="wa-phone-id" />
        <In label={tr("crm.display_phone")} dir="ltr" value={d.display_phone || ""} onChange={set("display_phone")} testid="wa-display-phone" />
      </div>
      <SecretField label={tr("crm.token")} saved={d.token_saved} value={tok} onChange={setTok} testid="wa-token" onRemove={() => save({ remove: ["token"] })} />
      <SecretField label={tr("crm.app_secret")} saved={d.app_secret_saved} value={secret} onChange={setSecret} testid="wa-app-secret" onRemove={() => save({ remove: ["app_secret"] })} />
      <div className="text-sm space-y-1 border border-[var(--divider)] p-3">
        <div>{tr("crm.webhook_url")}: <span className="font-mono break-all" data-testid="wa-webhook-url">{d.webhook_url}</span> <button className="btn-ghost text-xs" onClick={() => copy(d.webhook_url)} data-testid="wa-copy-url"><Copy size={12} /></button></div>
        <div>{tr("crm.verify_token")}: <span className="font-mono break-all" data-testid="wa-verify-token">{d.verify_token}</span> <button className="btn-ghost text-xs" onClick={() => copy(d.verify_token)} data-testid="wa-copy-token"><Copy size={12} /></button></div>
        <div data-testid="wa-status">{d.connected ? tr("crm.connected") : tr("crm.not_connected")}{d.mock_mode ? ` (${tr("crm.test_mode")})` : ""}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" disabled={!d.crm} onClick={doSave} data-testid="wa-save">{tr("crm.save")}</button>
        <button className="btn-secondary" disabled={!d.crm} onClick={() => run("/crm/whatsapp/test")} data-testid="wa-test">{tr("crm.test_connection")}</button>
        <button className="btn-secondary" disabled={!d.crm} onClick={() => run("/crm/whatsapp/sync-templates")} data-testid="wa-sync">{tr("crm.sync_templates")}</button>
      </div>
      {result && <div className={`text-sm p-2 ${result.ok ? "bg-emerald-50" : "bg-red-50"}`} role="status" data-testid="wa-result">{result.message || tr("crm.templates_found", { p0: result.count })}</div>}
      <h3 className="font-semibold pt-2">{tr("crm.auto_reply")}</h3>
      <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!ar.enabled} onChange={(e) => setAr("enabled", e.target.checked)} data-testid="wa-auto-reply-on" />{tr("crm.auto_reply_on")}</label>
      {["welcome_en", "welcome_ar", "away_en", "away_ar"].map((k) => (
        <label key={k} className="block text-sm">{tr(`crm.ar_${k /* i18n-keys: welcome_en,welcome_ar,away_en,away_ar */}`)}
          <textarea className="input w-full mt-1" dir={k.endsWith("ar") ? "rtl" : "ltr"} rows={2} value={ar[k] || ""} onChange={(e) => setAr(k, e.target.value)} data-testid={`wa-${k}`} /></label>
      ))}
      <h3 className="font-semibold pt-2">{tr("crm.template_map")}</h3>
      {!names.length && <p className="text-sm text-amber-800" data-testid="wa-no-templates">{tr("crm.no_templates")}</p>}
      <div className="space-y-2">
        {(d.events || []).map((ev) => (
          <div key={ev} className="grid sm:grid-cols-4 gap-2 items-center text-sm" data-testid={`wa-map-${ev}`}>
            <span>{tr(`crm.ev_${ev /* i18n-keys: enquiry_received,salesperson_assigned,test_drive_booked,test_drive_reminder,advance_received,full_payment,sale_completed,ready_for_delivery */}`)}</span>
            {["template_en", "template_ar"].map((k) => (
              <select key={k} className="input h-9" value={map[ev]?.[k] || ""} onChange={(e) => setMap(ev, k, e.target.value)} data-testid={`wa-map-${ev}-${k}`}>
                <option value="">{k === "template_en" ? tr("crm.tpl_en") : tr("crm.tpl_ar")}</option>
                {names.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            ))}
            <input className="input h-9" placeholder={tr("crm.vars_ph")} value={(map[ev]?.vars || []).join(",")} onChange={(e) => setMap(ev, "vars", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={`wa-map-${ev}-vars`} />
            {!map[ev]?.template_en && <span className="sm:col-span-4 text-xs text-amber-800">{tr("crm.no_template_warn")}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function NotificationsSettingsPanel() {
  const [d, setD, save] = useCfg("/crm/notifications");
  const [smtp, setSmtp, saveSmtp] = useCfg("/crm/smtp");
  const [pw, setPw] = useState(undefined);
  const [testTo, setTestTo] = useState("");
  const [testRes, setTestRes] = useState(null);
  if (!d || !smtp) return null;
  const setE = (ev, k, v) => setD({ ...d, events: { ...d.events, [ev]: { ...d.events[ev], [k]: v } } });
  const ss = (k) => (e) => setSmtp({ ...smtp, [k]: e.target.value });
  return (
    <div className="space-y-6 max-w-3xl" data-testid="notifications-settings">
      {!d.crm && <Locked />}
      <section className="space-y-3" data-testid="smtp-settings">
        <h3 className="font-semibold">{tr("crm.email_title")}</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <In label={tr("crm.smtp_host")} value={smtp.host || ""} onChange={ss("host")} testid="smtp-host" />
          <In label={tr("crm.smtp_port")} type="number" value={smtp.port || 587} onChange={ss("port")} testid="smtp-port" />
          <label className="block text-sm">{tr("crm.smtp_tls")}
            <select className="input h-10 w-full mt-1" value={smtp.tls || "starttls"} onChange={ss("tls")} data-testid="smtp-tls">
              <option value="starttls">{tr("crm.tls_starttls")}</option><option value="ssl">{tr("crm.tls_ssl")}</option><option value="none">{tr("crm.none")}</option>
            </select></label>
          <In label={tr("crm.smtp_user")} value={smtp.username || ""} onChange={ss("username")} testid="smtp-user" />
          <In label={tr("crm.from_name")} value={smtp.from_name || ""} onChange={ss("from_name")} testid="smtp-from-name" />
          <In label={tr("crm.from_address")} dir="ltr" value={smtp.from_address || ""} onChange={ss("from_address")} testid="smtp-from-address" />
        </div>
        <SecretField label={tr("crm.smtp_password")} saved={smtp.password_saved} value={pw} onChange={setPw} testid="smtp-password" onRemove={() => saveSmtp({ remove_password: true })} />
        <div className="flex flex-wrap gap-2 items-end">
          <button className="btn-primary" data-testid="smtp-save" onClick={async () => { const b = { host: smtp.host, port: Number(smtp.port || 587), tls: smtp.tls || "starttls", username: smtp.username, from_name: smtp.from_name, from_address: smtp.from_address }; if (pw) b.password = pw; if (await saveSmtp(b)) setPw(undefined); }}>{tr("crm.save")}</button>
          <In label={tr("crm.test_to")} dir="ltr" value={testTo} onChange={(e) => setTestTo(e.target.value)} testid="smtp-test-to" />
          <button className="btn-secondary" data-testid="smtp-test" onClick={async () => { try { const { data } = await api.post("/crm/smtp/test", { to: testTo }); setTestRes(data); } catch (e) { toast.error(formatApiError(e)); } }}>{tr("crm.send_test")}</button>
        </div>
        {testRes && <div className={`text-sm p-2 ${testRes.status === "sent" ? "bg-emerald-50" : "bg-red-50"}`} role="status" data-testid="smtp-test-result">{testRes.status}{testRes.error ? `: ${testRes.error}` : ""}</div>}
      </section>
      <section className="space-y-3">
        <h3 className="font-semibold">{tr("crm.events_title")}</h3>
        <p className="text-xs opacity-70">{tr("crm.vars_help")}: {d.vars.map((v) => `{${v}}`).join(" ")}</p>
        {Object.entries(d.events).map(([ev, e]) => (
          <details key={ev} className="border border-[var(--divider)] p-3" data-testid={`notify-${ev}`}>
            <summary className="cursor-pointer text-sm flex flex-wrap items-center gap-3">
              <span className="font-medium">{tr(`crm.ev_${ev /* i18n-keys: enquiry_received,salesperson_assigned,test_drive_booked,test_drive_reminder,advance_received,full_payment,sale_completed,ready_for_delivery */}`)}</span>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={e.email} onChange={(x) => setE(ev, "email", x.target.checked)} data-testid={`notify-${ev}-email`} />{tr("crm.ch_email")}</label>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={e.whatsapp} onChange={(x) => setE(ev, "whatsapp", x.target.checked)} data-testid={`notify-${ev}-whatsapp`} />{tr("crm.ch_whatsapp")}</label>
              {d.missing_template.includes(ev) && e.whatsapp && <span className="text-xs text-amber-800">{tr("crm.no_template_warn")}</span>}
            </summary>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {["subject_en", "subject_ar", "body_en", "body_ar"].map((k) => (
                <label key={k} className="block text-xs">{tr(`crm.f_${k /* i18n-keys: subject_en,subject_ar,body_en,body_ar */}`)}
                  <textarea className="input w-full mt-1" rows={k.startsWith("body") ? 3 : 1} dir={k.endsWith("ar") ? "rtl" : "ltr"} value={e[k]} onChange={(x) => setE(ev, k, x.target.value)} data-testid={`notify-${ev}-${k}`} /></label>
              ))}
            </div>
          </details>
        ))}
        <button className="btn-primary" disabled={!d.crm} onClick={() => save({ events: d.events })} data-testid="notify-save">{tr("crm.save")}</button>
      </section>
    </div>
  );
}
